import React from 'react';
import { systemComponents } from '../data/mock';
import { Sun, BatteryCharging, Car, Thermometer, Cpu, CheckCircle, ArrowRight, ShieldCheck } from 'lucide-react';

const iconMap = {
  'sun': Sun,
  'battery-charging': BatteryCharging,
  'car': Car,
  'thermometer': Thermometer,
  'cpu': Cpu,
  'shield-check': ShieldCheck
};

const SolutionSection = () => {
  return (
    <section id="leistungen" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
            <span className="text-green-700 font-semibold text-sm">Die Lösung</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Ihr ganzheitliches
            <span className="text-sun-orange"> Energiesystem</span>
          </h2>
          
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Sunprojects liefert mehr als nur Solarmodule. Wir planen und installieren 
            komplette Energielösungen, die perfekt aufeinander abgestimmt sind.
          </p>
        </div>

        {/* System Components Diagram */}
        <div className="relative mb-16">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-[4.5rem] left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-sun-orange via-sun-blue to-green-500" />
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {systemComponents.map((component, index) => {
              const IconComponent = iconMap[component.icon];
              const colors = [
                { ring: 'ring-sun-orange', bg: 'bg-sun-orange', light: 'bg-sun-orange/10' },
                { ring: 'ring-sun-blue', bg: 'bg-sun-blue', light: 'bg-sun-blue/10' },
                { ring: 'ring-amber-500', bg: 'bg-amber-500', light: 'bg-amber-100' },
                { ring: 'ring-emerald-500', bg: 'bg-emerald-500', light: 'bg-emerald-100' },
                { ring: 'ring-violet-500', bg: 'bg-violet-500', light: 'bg-violet-100' },
                { ring: 'ring-cyan-500', bg: 'bg-cyan-500', light: 'bg-cyan-100' }
              ][index];
              
              return (
                <div key={index} data-testid={`system-component-${index}`} className="relative group">
                  <div className="bg-white rounded-2xl p-5 border-2 border-slate-100 hover:border-sun-orange/40 hover:shadow-xl transition-all duration-300 h-full text-center">
                    {/* Connector Dot - Desktop */}
                    <div className={`hidden lg:block absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 ${colors.bg} rounded-full ring-4 ring-white shadow-lg`} />
                    
                    <div className={`w-16 h-16 mx-auto mb-4 ${colors.light} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className={`w-8 h-8 ${colors.bg.replace('bg-', 'text-')}`} />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2 text-base">{component.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{component.description}</p>
                  </div>
                  
                  {/* Arrow - Mobile */}
                  {index < systemComponents.length - 1 && index !== 1 && index !== 3 && (
                    <div className="lg:hidden flex justify-center my-3">
                      <ArrowRight className="w-5 h-5 text-slate-300" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Benefits Bar */}
        <div className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 rounded-3xl p-10 text-white shadow-2xl">
          <div className="grid md:grid-cols-3 gap-10 text-center">
            <div className="group">
              <div className="text-5xl font-bold text-sun-orange mb-3 group-hover:scale-110 transition-transform">bis 85%</div>
              <div className="text-white/80 text-lg">Eigenverbrauchsquote</div>
              <div className="text-white/50 text-sm mt-1">mit intelligentem Energiemanagement</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold text-sun-orange mb-3 group-hover:scale-110 transition-transform">25+ Jahre</div>
              <div className="text-white/80 text-lg">Anlagenlebensdauer</div>
              <div className="text-white/50 text-sm mt-1">durch hochwertige Komponenten</div>
            </div>
            <div className="group">
              <div className="text-5xl font-bold text-sun-orange mb-3 group-hover:scale-110 transition-transform">8-10 Jahre</div>
              <div className="text-white/80 text-lg">Amortisationszeit</div>
              <div className="text-white/50 text-sm mt-1">bei aktuellen Strompreisen</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;
